import { money as makeMoney, type FormatMoneyOptions } from '@fd/money';
import {
  Baby,
  Banknote,
  BookOpen,
  Briefcase,
  Car,
  Coffee,
  Droplet,
  Dumbbell,
  Fuel,
  PiggyBank,
  Shirt,
  Smartphone,
  Wifi,
  Wrench,
  Zap,
  CircleDashed,
  Clapperboard,
  GraduationCap,
  Gift,
  HeartPulse,
  Home,
  Landmark,
  PawPrint,
  Plane,
  Receipt,
  Repeat,
  ShoppingBag,
  ShoppingBasket,
  TrainFront,
  Utensils,
  ArrowLeftRight,
  ClipboardCheck,
  Coins,
  Gamepad2,
  HandCoins,
  Package,
  Percent,
  Pill,
  Pizza,
  ReceiptText,
  Scissors,
  ShieldCheck,
  ShoppingCart,
  SquareParking,
  Stethoscope,
  createLucideIcon,
  type LucideIcon,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useT } from '../app/i18n.tsx';

// ------------------------------------------------------------------------------------ money

const reducedMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * A figure that glides to a new value (a changed period, a sync) instead of jumping. The first
 * value is shown as it is — nothing counts up from zero when a page opens.
 */
function useGlide(target: bigint, enabled: boolean): bigint {
  const [shown, setShown] = useState(target);
  const current = useRef(target);
  useEffect(() => {
    if (!enabled || reducedMotion() || current.current === target) {
      current.current = target;
      setShown(target);
      return;
    }
    const from = Number(current.current);
    const to = Number(target);
    const started = performance.now();
    const duration = 700;
    let frame = 0;
    const step = (now: number) => {
      const progress = Math.min(1, (now - started) / duration);
      const eased = 1 - Math.pow(1 - progress, 4);
      const value = progress >= 1 ? target : BigInt(Math.round(from + (to - from) * eased));
      current.current = value;
      setShown(value);
      if (progress < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [target, enabled]);
  return shown;
}

export function Money({
  minor,
  currency,
  tone = 'plain',
  options,
  className,
  animate = false,
}: {
  minor: bigint | string;
  currency: string;
  /** 'signed' colours income green and keeps expenses in primary ink (DESIGN_SYSTEM §4). */
  tone?: 'plain' | 'signed' | 'muted';
  options?: FormatMoneyOptions;
  className?: string;
  /** Glide to new values (headline figures only). */
  animate?: boolean;
}) {
  const t = useT();
  const target = makeMoney(minor, currency);
  const glided = useGlide(target.minor, animate);
  const value = animate ? makeMoney(glided, currency) : target;
  const positive = target.minor > 0n;
  const text = t.money(value, { signDisplay: tone === 'signed' ? 'exceptZero' : 'auto', ...options });
  const colour = tone === 'signed' && positive ? 'fg-income' : tone === 'muted' ? 'text-muted' : '';
  return <span className={['money', colour, className].filter(Boolean).join(' ')}>{text}</span>;
}

// ------------------------------------------------------------------------------------ cards

export function Card({
  title,
  subtitle,
  action,
  children,
  className,
  bodyClassName,
  flush,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  flush?: boolean;
}) {
  return (
    <section className={['card', className].filter(Boolean).join(' ')}>
      {(title || action) && (
        <header className="card__header">
          <div className="stack">
            {title && <h2 className="card__title">{title}</h2>}
            {subtitle && <span className="card__subtitle">{subtitle}</span>}
          </div>
          {action}
        </header>
      )}
      {flush ? children : <div className={['card__body', bodyClassName].filter(Boolean).join(' ')}>{children}</div>}
    </section>
  );
}

export function Skeleton({ width = '100%', height = 14, radius }: { width?: number | string; height?: number; radius?: number }) {
  return <span className="skeleton" style={{ width, height, borderRadius: radius }} aria-hidden="true" />;
}

/**
 * A page still reading its data: the shapes of what is coming (figures as tiles, then cards with
 * lines), never an empty page and never a spinner in the middle of it (the owner's brief).
 */
export function PageSkeleton({ tiles = 4, cards = 2 }: { tiles?: number; cards?: number }) {
  return (
    <div className="page-skeleton" aria-busy="true">
      {tiles > 0 && (
        <div className="page-skeleton__tiles" style={{ gridTemplateColumns: `repeat(${tiles}, minmax(0, 1fr))` }}>
          {Array.from({ length: tiles }, (_, i) => (
            <div key={i} className="page-skeleton__tile">
              <Skeleton width={90} height={13} />
              <Skeleton width="60%" height={26} />
            </div>
          ))}
        </div>
      )}
      {Array.from({ length: cards }, (_, i) => (
        <div key={i} className="card page-skeleton__card">
          <Skeleton width={160} height={16} />
          {Array.from({ length: 4 }, (_, j) => (
            <div key={j} className="page-skeleton__line">
              <Skeleton width={32} height={32} radius={10} />
              <Skeleton width={`${55 - j * 7}%`} height={14} />
              <Skeleton width={80} height={14} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  text,
  actions,
}: {
  icon: ReactNode;
  title: string;
  text?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="empty" role="status">
      {/* A small composition of interface shapes: two resting cards and the subject on a badge. */}
      <div className="empty__art" aria-hidden="true">
        <span className="empty__card empty__card--back" />
        <span className="empty__card empty__card--front">
          <i />
          <i />
          <i />
        </span>
        <span className="empty__icon">{icon}</span>
      </div>
      <div className="empty__title">{title}</div>
      {text && <div className="empty__text">{text}</div>}
      {actions && <div className="empty__actions">{actions}</div>}
    </div>
  );
}

// ------------------------------------------------------------------------------------ people & categories

export function Avatar({ name, color, size }: { name: string; color: string | null; size?: 'lg' }) {
  const initials = name
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return (
    <span className={`avatar${size === 'lg' ? ' avatar--lg' : ''}`} style={{ background: color ?? '#898781' }} aria-hidden="true">
      {initials}
    </span>
  );
}

/** Lucide has no tooth (the owner: "czemu dentysta ma stetoskop?"); drawn in its style, 24 × 24 with round 2 px strokes. */
const Tooth = createLucideIcon('tooth', [
  [
    'path',
    {
      d: 'M7 3C4.8 3 3 4.8 3 7.2c0 2.3 1 3.8 1.6 5.6.5 1.6.7 3.4 1.1 5.2.3 1.4.8 3 2 3 1.4 0 1.6-2 1.9-3.6.3-1.4.6-2.9 1.4-2.9s1.1 1.5 1.4 2.9c.3 1.6.5 3.6 1.9 3.6 1.2 0 1.7-1.6 2-3 .4-1.8.6-3.6 1.1-5.2.6-1.8 1.6-3.3 1.6-5.6C21 4.8 19.2 3 17 3c-1.9 0-3 1-5 1S8.9 3 7 3z',
      key: 'tooth-outline',
    },
  ],
]);

export const CATEGORY_ICONS: Record<string, LucideIcon> = {
  home: Home,
  'shopping-basket': ShoppingBasket,
  utensils: Utensils,
  'train-front': TrainFront,
  'shopping-bag': ShoppingBag,
  clapperboard: Clapperboard,
  'heart-pulse': HeartPulse,
  receipt: Receipt,
  repeat: Repeat,
  plane: Plane,
  baby: Baby,
  'paw-print': PawPrint,
  landmark: Landmark,
  gift: Gift,
  'graduation-cap': GraduationCap,
  briefcase: Briefcase,
  'circle-dashed': CircleDashed,
  transfer: ArrowLeftRight,
  // For the owner's own categories.
  car: Car,
  fuel: Fuel,
  droplet: Droplet,
  zap: Zap,
  wifi: Wifi,
  smartphone: Smartphone,
  dumbbell: Dumbbell,
  coffee: Coffee,
  shirt: Shirt,
  'piggy-bank': PiggyBank,
  wrench: Wrench,
  'book-open': BookOpen,
  banknote: Banknote,
  // Icons that say what a category is (29.09, the owner: "ikonki odpowiadające nazwie kategorii").
  'shopping-cart': ShoppingCart,
  pizza: Pizza,
  package: Package,
  stethoscope: Stethoscope,
  scissors: Scissors,
  pill: Pill,
  'gamepad-2': Gamepad2,
  percent: Percent,
  'square-parking': SquareParking,
  'shield-check': ShieldCheck,
  'hand-coins': HandCoins,
  'receipt-text': ReceiptText,
  'clipboard-check': ClipboardCheck,
  coins: Coins,
  tooth: Tooth,
};

/** Relative luminance of a "#rrggbb" colour, or null for anything else. */
function luminance(hex: string): number | null {
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return null;
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * A category's mark as an app-icon tile, like the shops' logos (the owner): the category's colour
 * filled in, a slight sheen, the glyph white — or dark on a light colour (yellow, amber), where
 * white would not read (under 3:1).
 */
export function CategoryIcon({ icon, color }: { icon: string | null; color: string | null }) {
  const Icon = CATEGORY_ICONS[icon ?? ''] ?? CircleDashed;
  const tint = color ?? '#898781';
  const light = (luminance(tint) ?? 0) > 0.3;
  return (
    <span
      className="cat-icon"
      style={{ background: `linear-gradient(150deg, color-mix(in srgb, ${tint} 78%, #ffffff), ${tint} 70%)`, color: light ? '#1d1b16' : '#ffffff' }}
      aria-hidden="true"
    >
      <Icon />
    </span>
  );
}

// ------------------------------------------------------------------------------------ segmented

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: readonly { value: T; label: string }[];
  onChange(value: T): void;
  label: string;
}) {
  // The raised thumb slides to the chosen option rather than reappearing under it.
  const id = useId();
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button key={option.value} type="button" aria-pressed={option.value === value} onClick={() => onChange(option.value)}>
          {option.value === value && <motion.span layoutId={`segmented-${id}`} className="segmented__thumb" transition={{ type: 'spring', stiffness: 520, damping: 42, mass: 0.7 }} />}
          <span className="segmented__label">{option.label}</span>
        </button>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------------------------ modal

/**
 * While a panel is open the page under it does not scroll (a phone would otherwise move the
 * page when the panel reaches its end). Nested panels count, so the lock ends with the last one.
 */
let locks = 0;
export function useScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    locks += 1;
    const root = document.documentElement;
    root.classList.add('scroll-locked');
    return () => {
      locks -= 1;
      if (locks === 0) root.classList.remove('scroll-locked');
    };
  }, [active]);
}

export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  width,
}: {
  open: boolean;
  title: string;
  onClose(): void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}) {
  const panel = useRef<HTMLDivElement | null>(null);
  useScrollLock(open);
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    addEventListener('keydown', onKey);
    const first = panel.current?.querySelector<HTMLElement>('input, select, textarea, button');
    first?.focus();
    return () => {
      removeEventListener('keydown', onKey);
      previous?.focus();
    };
  }, [open, onClose]);
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) onClose();
          }}
        >
          <motion.div
            ref={panel}
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-label={title}
            style={width ? { width: `min(${width}px, 100%)` } : undefined}
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.2, ease: [0.2, 0, 0, 1] }}
          >
            <div className="modal__header">
              <h2 className="modal__title">{title}</h2>
            </div>
            <div className="modal__body">{children}</div>
            {footer && <div className="modal__footer">{footer}</div>}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ------------------------------------------------------------------------------------ toasts

type Toast = { id: number; text: string; icon?: ReactNode };
const ToastContext = createContext<(text: string, icon?: ReactNode) => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const show = useCallback((text: string, icon?: ReactNode) => {
    const id = Date.now() + Math.random();
    setToasts((list) => [...list, { id, text, icon }]);
    setTimeout(() => setToasts((list) => list.filter((toast) => toast.id !== id)), 3200);
  }, []);
  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className="toast-region" role="status" aria-live="polite">
        <AnimatePresence>
          {toasts.map((toast) => (
            <motion.div
              key={toast.id}
              className="toast"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              transition={{ duration: 0.18 }}
            >
              {toast.icon}
              {toast.text}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
